import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Searchbar, Text } from 'react-native-paper';
import type { EdgeInsets } from 'react-native-safe-area-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ArrowBackIcon, CloseIcon } from '@/assets';
import { MAX_SEARCH_LENGTH } from '@/constants/search';
import { useSearchPost } from '@/features/api/hooks/grapevine-post/useSearchPost';
import { SimplePostList } from '@/features/posts/components/SimplePostList';
import { SIZE } from '@/features/posts/constants/params';
import type { PostsRoute } from '@/features/posts/navigation/postsRoute';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type { AppNativeStackScreenProps } from '@/navigation/types/navigation';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme, insets: EdgeInsets) =>
  StyleSheet.create({
    container: {
      flex: 1,
      paddingTop: insets.top,
      backgroundColor: theme.colors.surfaceContainerLow
    },
    hintText: {
      color: theme.colors.onSurfaceVariant,
      marginVertical: 24,
      marginHorizontal: 16
    }
  });

type Props = AppNativeStackScreenProps<typeof PostsRoute.PostSearch>;

export const PostSearchScreen: FC<Props> = ({ navigation }) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme, insets), [insets, theme]);

  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'screens.post-search'
  });

  const [search, setSearch] = useState<string>('');

  const {
    data: posts,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch,
    isRefetching
  } = useSearchPost({
    count: SIZE,
    search: search
  });

  return (
    <View style={styles.container}>
      <Searchbar
        // oxlint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        mode="view"
        placeholder={t('placeholder')}
        value={search}
        maxLength={MAX_SEARCH_LENGTH}
        onChangeText={setSearch}
        loading={isLoading}
        onIconPress={() => navigation.pop()}
        icon={({ color, size }) => (
          <ArrowBackIcon fill={color} width={size} height={size} />
        )}
        clearIcon={({ color, size }) =>
          !isEmptyOrNull(search) && (
            <CloseIcon fill={color} width={size} height={size} />
          )
        }
        style={{ backgroundColor: theme.colors.surfaceContainerLow }}
      />
      {isEmptyOrNull(search) && (
        <Text variant="bodyLarge" style={styles.hintText}>
          {t('hint')}
        </Text>
      )}
      <SimplePostList
        posts={posts}
        isLoading={isLoading}
        isError={isError}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetching={isFetching}
        isFetchingNextPage={isFetchingNextPage}
        refetch={refetch}
        isRefetching={isRefetching}
        disableGradient
        hideEndView={search.length < 3}
        hideEmptyView={search.length < 3}
      />
    </View>
  );
};
